import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicLayout from './PublicLayout';
import Icon from './Icon';
import { ContentPage, PageSlug, formatEffectiveDate, getPage } from '../api/pages';

interface LegalPageProps {
  slug: PageSlug;
  /** Shown in the tab and heading until the page loads. */
  fallbackTitle: string;
}

interface Heading {
  id: string;
  text: string;
}

const slugify = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'section';

/**
 * Gives every <h2> in the admin-edited HTML a stable id so the table of
 * contents can link to it. The HTML is sanitized by the API on save.
 */
const withHeadingIds = (html: string): { html: string; headings: Heading[] } => {
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild as HTMLElement;
  const used = new Set<string>();
  const headings: Heading[] = [];
  root.querySelectorAll('h2').forEach((h) => {
    const text = (h.textContent || '').trim();
    if (!text) return;
    let id = slugify(text);
    for (let n = 2; used.has(id); n += 1) id = `${slugify(text)}-${n}`;
    used.add(id);
    h.id = id;
    headings.push({ id, text });
  });
  return { html: root.innerHTML, headings };
};

// App screenshots for the Delete Account page. They sit outside the admin-edited
// content because the editor and the API's HTML allow-list don't keep images.
const DELETE_ACCOUNT_SHOTS = [
  {
    src: '/images/delete-account/step-1-profile.png',
    alt: 'Profile tab in the Rentagade app with the Delete Account button below Log Out',
    caption: (
      <>
        On the <strong>Profile</strong> tab, tap <strong>Delete Account</strong>.
      </>
    ),
  },
  {
    src: '/images/delete-account/step-2-confirm.png',
    alt: 'Delete Account confirmation box with Cancel and Delete buttons',
    caption: (
      <>
        Tap <strong>Delete</strong> to confirm, or <strong>Cancel</strong> to keep your account.
      </>
    ),
  },
];

const AppScreenshots: React.FC = () => (
  <section className="card p-6 sm:p-8 mb-6" aria-labelledby="in-the-app">
    <h2 id="in-the-app" className="text-lg font-bold text-ink mb-1">
      In the app
    </h2>
    <p className="text-sm text-g500 mb-6">Two taps from the Profile tab.</p>
    <div className="grid grid-cols-2 gap-4 sm:gap-8 max-w-xl mx-auto">
      {DELETE_ACCOUNT_SHOTS.map((shot, i) => (
        <figure key={shot.src} className="m-0">
          <img
            src={shot.src}
            alt={shot.alt}
            width={540}
            height={1195}
            loading="lazy"
            className="w-full h-auto rounded-[22px] border-[5px] border-ink shadow-md bg-white"
          />
          <figcaption className="flex items-start gap-2 mt-3 text-[13px] sm:text-sm text-g600 leading-snug">
            <span className="flex-none w-6 h-6 rounded-full bg-brand text-white text-xs font-bold grid place-items-center">
              {i + 1}
            </span>
            <span>{shot.caption}</span>
          </figcaption>
        </figure>
      ))}
    </div>
  </section>
);

/** Public Privacy Policy / Terms page; content is managed in the admin console. */
const LegalPage: React.FC<LegalPageProps> = ({ slug, fallbackTitle }) => {
  const navigate = useNavigate();
  const [page, setPage] = useState<ContentPage | null>(null);
  const [error, setError] = useState<boolean>(false);

  const load = useCallback(async () => {
    setError(false);
    setPage(null);
    try {
      setPage(await getPage(slug));
    } catch {
      setError(true);
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  const body = useMemo(() => (page ? withHeadingIds(page.content) : null), [page]);

  // Same-site links in the content (e.g. Terms -> /privacy-policy) navigate
  // inside the app instead of reloading the page.
  const onContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const a = (e.target as HTMLElement).closest('a');
    const href = a?.getAttribute('href');
    if (!a || !href || !href.startsWith('/') || href.startsWith('//') || a.target === '_blank') return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    navigate(href);
  };

  const title = page?.title || fallbackTitle;

  return (
    <PublicLayout title={title} hideNav>
      <div className="max-w-3xl mx-auto px-4 pt-10">
        <div className="text-[12px] font-bold uppercase tracking-wider text-brand mb-2">
          {slug === 'delete-account' ? 'Account' : 'Legal'}
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-ink mb-2">{title}</h1>

        {error ? (
          <div className="card p-6 mt-6 text-center">
            <Icon name="info" size={22} color="var(--g400)" />
            <p className="text-g600 mt-2 mb-4">We couldn't load this page. Please try again.</p>
            <button className="btn btn-secondary btn-sm" onClick={load}>
              Try again
            </button>
          </div>
        ) : !page || !body ? (
          <div className="card p-6 sm:p-8 mt-6 animate-pulse" aria-busy="true" aria-label="Loading">
            {[80, 95, 70, 90, 60].map((w, i) => (
              <div key={i} className="h-3 rounded bg-g100 mb-4" style={{ width: `${w}%` }} />
            ))}
          </div>
        ) : (
          <>
            {page.effectiveDate ? (
              <p className="text-sm text-g500 mb-8">Effective {formatEffectiveDate(page.effectiveDate)}</p>
            ) : (
              <div className="mb-8" />
            )}

            {slug === 'delete-account' && <AppScreenshots />}

            {body.headings.length > 1 && (
              <nav className="card p-5 mb-6">
                <div className="text-sm font-bold text-ink mb-3">Contents</div>
                <ol className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm list-decimal list-inside text-g600">
                  {body.headings.map((h) => (
                    <li key={h.id}>
                      <a href={`#${h.id}`} className="hover:text-brand">
                        {h.text}
                      </a>
                    </li>
                  ))}
                </ol>
              </nav>
            )}

            <div
              className="card p-6 sm:p-8 legal-prose legal-prose-numbered"
              onClick={onContentClick}
              dangerouslySetInnerHTML={{ __html: body.html }}
            />
          </>
        )}
      </div>
    </PublicLayout>
  );
};

export default LegalPage;
